import type { IconDefinition } from "@outschool/icons";
import {
  faCrownRegular,
  faElephant,
  faFire,
  faOctopus,
  faPawClaws,
  faRobot,
  faRocket,
  faUnicorn,
  faUser,
} from "@outschool/icons";

import eagleIcon from "./avatar-animals/eagle.svg";
import monsterIcon from "./avatar-animals/monster.svg";
import sharkIcon from "./avatar-animals/shark.svg";
import tigerIcon from "./avatar-animals/tiger.svg";
import wolfIcon from "./avatar-animals/wolf.svg";

export const CREATURE_TYPES: ReadonlyArray<{
  value: string;
  label: string;
  icon: IconDefinition | string;
}> = [
  { value: "human", label: "Human", icon: faUser },
  { value: "monster", label: "Monster", icon: monsterIcon },
  { value: "robot", label: "Robot", icon: faRobot },
  { value: "alien", label: "Alien", icon: faRocket },
  { value: "lion", label: "Lion", icon: faCrownRegular },
  { value: "eagle", label: "Eagle", icon: eagleIcon },
  { value: "shark", label: "Shark", icon: sharkIcon },
  { value: "elephant", label: "Elephant", icon: faElephant },
  { value: "tiger", label: "Tiger", icon: tigerIcon },
  { value: "bear", label: "Bear", icon: faPawClaws },
  { value: "wolf", label: "Wolf", icon: wolfIcon },
  { value: "octopus", label: "Octopus", icon: faOctopus },
  { value: "dragon", label: "Dragon", icon: faFire },
  { value: "unicorn", label: "Unicorn", icon: faUnicorn },
];

export const AVATAR_STYLES = [
  {
    value: "fantastical",
    label: "Fantastical",
    description: "Magical and whimsical with sparkles and fantasy elements",
  },
  {
    value: "simple",
    label: "Simple",
    description: "Stylized and clean with balanced realism",
  },
  {
    value: "cartoon",
    label: "Cartoon",
    description: "Bold, playful cartoon style",
  },
  {
    value: "line-art",
    label: "Line Art",
    description: "Outlined sketch style",
  },
  {
    value: "pixel",
    label: "Pixels",
    description: "Retro 16-bit pixel art style",
  },
  {
    value: "graphic",
    label: "Graphic",
    description: "Bold modern graphic design",
  },
  {
    value: "anime",
    label: "Anime",
    description: "Japanese manga/anime style",
  },
  {
    value: "surrealist",
    label: "Surrealist",
    description: "Dream-like fantastical imagery",
  },
] as const;

export const OUTDOOR_BACKGROUNDS = [
  "forest",
  "space",
  "playground",
  "beach",
  "mountains",
  "city",
  "underwater",
  "garden",
  "cave",
  "jungle",
  "snow",
  "savanna",
] as const;

export const INDOOR_BACKGROUNDS = [
  "none",
  "classroom",
  "lab",
  "library",
  "art-studio",
  "museum",
] as const;

export const GENDER_OPTIONS = [
  { value: "none", label: "They/Them" },
  { value: "boy", label: "He/Him" },
  { value: "girl", label: "She/Her" },
] as const;

/** Image framing for human avatars (passed to startAvatarGeneration as viewType) */
export const VIEW_TYPE_OPTIONS = [
  { value: "full-body" as const, label: "Full body" },
  { value: "headshot" as const, label: "Head" },
] as const;

export type AvatarViewType = (typeof VIEW_TYPE_OPTIONS)[number]["value"];

export const AGE_RANGE_OPTIONS = [
  { value: "baby", label: "Baby" },
  { value: "toddler", label: "Toddler" },
  { value: "littleKid", label: "Little kid" },
  { value: "bigKid", label: "Big kid" },
] as const;

export const HAIR_COLORS = [
  "#2C1B18",
  "#3D2314",
  "#654321",
  "#8B4513",
  "#D2691E",
  "#CD853F",
  "#F5DEB3",
  "#C0C0C0",
  "#FFFFFF",
  "#FF1493",
  "#9400D3",
  "#00CED1",
  "#32CD32",
  "#FF4500",
  "#4169E1",
];

export const SKIN_COLORS = [
  "#FFDBCD",
  "#FDBCB4",
  "#F5DEB3",
  "#F1C27D",
  "#E0AC69",
  "#DEB887",
  "#D2B48C",
  "#CD853F",
  "#C68642",
  "#BC8F8F",
  "#A0522D",
  "#8B4513",
  "#8D5524",
  "#6B4423",
  "#654321",
  "#3D2817",
  "#E6E6FA",
  "#98FB98",
  "#87CEEB",
];
